import React, { useState, useEffect } from 'react';
import { pushDataLayer } from '../gtm';
import axios from 'axios';
import StepProgressBar from '../components/StepProgressBar';
import Header from '../components/Header';
import Step0 from '../components/StepIntro';
import Step1 from '../components/Step1_NombreApellido';
import Step2 from '../components/Step2_FechaNacimiento';
import Step3 from '../components/Step3_Contacto';
import Step4 from '../components/Step4_CedulaDepartamento';
import Step5 from '../components/Step5_EstudioOrigen';
import Step6 from '../components/Step6_MateriasPendientes';
import Step7 from '../components/Step7_MateriasEspecificas';
import Step8 from '../components/Step7_Preferencias';
import StepFinal from '../components/StepFinal_Gracias';
import StepFinalRechazado from '../components/StepFinal_Rechazado';
import StepFinalRechazadoPrivado from '../components/StepFinal_RechazadoPrivado';
import StepFinalIncertidumbre from '../components/StepFinal_Incertidumbre';
import StepFinalMayorEdad from '../components/StepFinal_MayorEdad';
import StepFinalMenorEdad from '../components/StepFinal_MenorEdad';
import StepFinalBachilleratoCompleto from '../components/StepFinal_BachilleratoCompleto';
import StepFinalMateriasUtu from '../components/StepFinal_MateriasUtu';
import StepFinalOrientacionUtu from '../components/StepFinal_OrientacionUtu';
import PopupRequisitos from '../components/PopupRequisitos';
import { calculatePendingTotal, getEndingCase } from '../helpers/eligibility';
import { getLeadEntryUrl, persistEntryUrl } from '../helpers/entryUrl';
import Swal from 'sweetalert2'; 


const steps = [
  Step0, Step1, Step3, Step4, Step2, Step5, Step6, Step7, Step8,
  StepFinal, StepFinalRechazado, StepFinalIncertidumbre, StepFinalMayorEdad,
  StepFinalMenorEdad, StepFinalRechazadoPrivado, StepFinalBachilleratoCompleto,
  StepFinalMateriasUtu, StepFinalOrientacionUtu
];

const endingConfig = {
  2: { toStep: 10, estado: 'rechazado_materias', motivoRechazo: 'exceso_materias_pendientes' },
  3: { toStep: 11, estado: 'rechazado_incertidumbre', motivoRechazo: 'no_recuerda_materias' },
  4: { toStep: 12, estado: 'rechazado_mayor_edad', motivoRechazo: 'mayor_de_edad' },
  5: { toStep: 13, estado: 'rechazado_menor_edad', motivoRechazo: 'menor_de_edad' },
  6: { toStep: 14, estado: 'rechazado_privado', motivoRechazo: 'institucion_privada' },
  7: { toStep: 15, estado: 'rechazado_sin_materias', motivoRechazo: 'bachillerato_finalizado' },
  8: { toStep: 16, estado: 'rechazado_materias_utu', motivoRechazo: 'exceso_materias_utu' },
  9: { toStep: 17, estado: 'rechazado_orientacion_utu', motivoRechazo: 'orientacion_utu_sin_cobertura' }
};

const initialFormData = {
  nombre: '',
  apellido: '',
  nacimiento: '',
  telefono: '',
  otroTelefono: '',
  email: '',
  cedula: '',
  departamento: '',
  barrio: '',
  tipoCentro: '',
  tipoInstitucion: '',
  bachillerato: '',
  orientacion5to: '',
  orientacion6to: '',
  pendientes4to: '',
  pendientes5to: '',
  pendientes6to: '',
  totalMaterias: 0,
  materiasPendientesSeleccionadas: {},
  modalidad: '',
  horario: ''
}; 



const HomePage = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    persistEntryUrl();
    // Evento paso1_home solo cuando se carga la página inicial
    pushDataLayer('paso1_home');
  }, []);

  const openPopup = () => setShowPopup(true);
  const dismissPopup = () => setShowPopup(false);

  const reset = () => {
    pushDataLayer('form_reset', { from_step: step });
    setFormData(initialFormData);
    setStep(0);
  };

  const submitForm = async (override = {}, { showError = true } = {}) => {
    if (submitting) {
      return false;
    }

    try {
      setSubmitting(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/form`, {
        ...formData,
        ...override,
        entryUrl: getLeadEntryUrl(),
      });
      const status = override.estado || 'completo';
      pushDataLayer('form_submit', { status });
      return true;
    } catch (error) {
      console.error(error);
      if (showError) {
        Swal.fire({
          icon: 'error',
          title: 'Falló el registro',
          text: 'No pudimos completar el registro. Intentá nuevamente.',
          confirmButtonText: 'Reintentar',
          confirmButtonColor: '#d33'
        });
      }
      pushDataLayer('form_submit', { status: 'error' });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const goToEnding = async (endingCase, override = {}) => {
    const config = endingConfig[endingCase];
    if (!config) return false;

    const saved = await submitForm({
      ...override,
      estado: config.estado,
      motivoRechazo: config.motivoRechazo
    }, { showError: false });
    setStep(config.toStep);
    return saved;
  };

  const next = async (dataOverride = {}) => {
    const currentFormData = {
      ...formData,
      ...dataOverride,
    };

    if (step === 5) {
      const endingCase = getEndingCase(currentFormData);
      if (endingCase === 6 || endingCase === 9) {
        await goToEnding(endingCase, dataOverride);
        return;
      }
    }

    if (step === 6) {
      const totalMaterias = calculatePendingTotal(currentFormData);
      const endingCase = getEndingCase(currentFormData);

      if (endingCase && endingCase !== 1) {
        await goToEnding(endingCase, { ...dataOverride, totalMaterias });
        return;
      }

      setFormData((previous) => ({
        ...previous,
        ...dataOverride,
        totalMaterias,
        estado: 'completo',
        motivoRechazo: ''
      }));
    }

    setStep(step + 1);
  };

  const prev = () => {
    setStep(step - 1);
  };

  const onSubmit = async (override = {}) => {
    if (submitting) return;
    const saved = await submitForm(override);
    if (saved) {
      await next(override);
    }
  };

  const StepComponent = steps[step];

  return (
    <>
      {showPopup && step === 0 && <PopupRequisitos onClose={dismissPopup} />}
      <StepProgressBar step={step} steps={steps} />
      <Header />
      <div className="fade-step">
        <StepComponent
          formData={formData}
          setFormData={setFormData}
          next={next}
          prev={prev}
          onSubmit={onSubmit}
          submitting={submitting}
          reset={reset}
          onVerRequisitos={openPopup}
          onMenorEdad={() => goToEnding(5)}
          onMayorEdad={() => goToEnding(4)}
        />
      </div>
    </>
  );
};

export default HomePage;
